import React, { useState, useEffect } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Swal from "sweetalert2";
import BASE_URL from "../BaseUrl/apiConfig";

const UpdateMessageLogModal = ({
  showModal,
  setShowModal,
  selectedMessageLog,
  updateData,
  resetForm,
}) => {
  const [type, setType] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [recipientEmailOrLink, setRecipientEmailOrLink] = useState("");
  const [startDate, setStartDate] = useState(new Date());
  const [messageStatus, setMessageStatus] = useState("");
  const [message, setMessage] = useState("");
  const [description, setDescription] = useState("");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setErrors({});
    if (showModal && selectedMessageLog) {
      setType(selectedMessageLog.type || "");
      setRecipientName(selectedMessageLog.recipientName || "");
      setRecipientEmailOrLink(selectedMessageLog.recipientEmailOrLink || "");
      setStartDate(new Date(selectedMessageLog.messageDate) || new Date());
      setMessageStatus(selectedMessageLog.messageStatus || "");
      setMessage(selectedMessageLog.message || "");
      setDescription(selectedMessageLog.description || "");
    }
  }, [showModal, selectedMessageLog]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "type") setType(value);
    if (name === "recipientName") setRecipientName(value);
    if (name === "recipientEmailOrLink") setRecipientEmailOrLink(value);
    if (name === "messageStatus") setMessageStatus(value);
    if (name === "description") setDescription(value);
  };

  const handleDateChange = (date) => {
    setStartDate(date);
  };

  const handleMessageChange = (value) => {
    setMessage(value);
  };

  const validateFields = () => {
    const newErrors = {};
    if (!type) newErrors.type = "Type is required";
    if (!recipientName) newErrors.recipientName = "Recipient Name is required";
    if (!recipientEmailOrLink) {
      newErrors.recipientEmailOrLink =
        "Recipient Email 'or' Profile Links are required";
    } else if (type === "Email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(recipientEmailOrLink)) {
        newErrors.recipientEmailOrLink = "Invalid email address";
      }
    }
    if (!startDate) newErrors.messageDate = "Message Date is required";

    // Ensure message is a string before checking its content
    const messageString = typeof message === "string" ? message : "";
    if (messageString.trim() === "<p><br></p>" || messageString.trim() === "") {
      newErrors.message = "Message content is required";
    }

    if (!messageStatus) newErrors.messageStatus = "Message Status is required";

    return newErrors; // Return the newErrors object
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const newErrors = validateFields();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const payload = {
      type,
      recipientName,
      recipientEmailOrLink,
      messageDate: startDate.toISOString(),
      messageStatus,
      message,
      description,
    };

    const userData = JSON.parse(localStorage.getItem("userData"));
    const userId = userData ? userData.userId : null;
    const groupId = userData ? userData.groupId : null;
    payload.userId = userId;
    payload.groupId = groupId;

    if (selectedMessageLog) {
      payload.messageID = selectedMessageLog.messageID;
    }

    fetch(`${BASE_URL}/api/Expense/AddUpdateMessageLog`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then((data) => {
        Swal.fire(
          "Success",
          "Message Log has been updated successfully.",
          "success"
        );
        handleCloseModal();
        updateData();

        setErrors("");
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  return (
    <>
      {showModal && (
        <>
          <div className="fixed inset-0 bg-gray-900 opacity-50 z-50"></div>
          <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white shadow-lg rounded-lg p-8 z-50">
            <h2 className="text-xl font-bold mb-4">Update Message Log</h2>
            <form onSubmit={handleSubmit} className="row">
              {/* Your form fields here */}
              <div className="form-group col-6 col-lg-3 mb-4">
                <select
                  className={`form-control custom-height ${
                    errors.type ? "is-invalid" : ""
                  }`}
                  name="type"
                  value={type}
                  onChange={handleInputChange}
                  title="Select the message type"
                >
                  <option value="">Select Type</option>
                  <option value="Email">Email</option>
                  <option value="LinkedIn Message">LinkedIn Message</option>
                  <option value="Facebook Message">Facebook Message</option>
                  <option value="Others">Others</option>
                </select>
                {errors.type && (
                  <div className="text-red-500">{errors.type}</div>
                )}
              </div>

              <div className="form-group col-6 col-lg-3 mb-4">
                <input
                  type="text"
                  name="recipientName"
                  placeholder="Recipient Name"
                  value={recipientName}
                  onChange={handleInputChange}
                  className={`form-control custom-height ${
                    errors.recipientName ? "is-invalid" : ""
                  }`}
                  title="Enter the recipient's name"
                />
                {errors.recipientName && (
                  <div className="text-red-500">{errors.recipientName}</div>
                )}
              </div>

              <div className="form-group col-12 col-lg-4 mb-4">
                <input
                  type="text"
                  name="recipientEmailOrLink"
                  placeholder="Recipient Email 'or' Profile Links"
                  value={recipientEmailOrLink}
                  onChange={handleInputChange}
                  className={`form-control custom-height ${
                    errors.recipientEmailOrLink ? "is-invalid" : ""
                  }`}
                  title="Enter the recipient's email or profile link"
                />
                {errors.recipientEmailOrLink && (
                  <div className="text-red-500">
                    {errors.recipientEmailOrLink}
                  </div>
                )}
              </div>

              <div className="form-group col-6 col-lg-2 mb-4">
                <DatePicker
                  selected={startDate}
                  onChange={handleDateChange}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  dateFormat="dd/MM/yyyy HH:mm"
                  className={`form-control custom-height ${
                    errors.messageDate ? "is-invalid" : ""
                  }`}
                  popperPlacement="bottom" // Always open below the input field
                  popperModifiers={{
                    preventOverflow: {
                      enabled: false, // Disable preventOverflow to avoid repositioning
                    },
                    hide: {
                      enabled: false, // Disable hide to ensure visibility
                    },
                  }}
                  title="Select date and time"
                />
                {errors.messageDate && (
                  <div className="text-red-500">{errors.messageDate}</div>
                )}
              </div>

              {/* <div className="form-group col-6 col-lg-2 mb-4">
                <select
                  className={`form-control custom-height ${
                    errors.messageStatus ? "is-invalid" : ""
                  }`}
                  name="messageStatus"
                  value={messageStatus}
                  onChange={handleInputChange}
                  title="Select the status of the message"
                >
                  <option value="">Select Status</option>
                  <option value="Primary Message">Primary Message</option>
                  <option value="Client Responded">Client Responded</option>
                  <option value="Our Response">Our Response</option>
                  <option value="Follow Up">Follow Up</option>
                  <option value="Closed">Closed</option>
                </select>
                {errors.messageStatus && (
                  <div className="text-red-500">{errors.messageStatus}</div>
                )}
              </div> */}

              <div className="form-group col-8 mb-4">
                <ReactQuill
                  theme="snow"
                  value={message}
                  onChange={handleMessageChange}
                  className={`quill-editor ${
                    errors.message ? "is-invalid" : ""
                  }`}
                  modules={{
                    toolbar: [
                      [{ header: "1" }, { header: "2" }, { font: [] }],
                      [{ size: [] }],
                      ["bold", "italic", "underline", "strike", "blockquote"],
                      [
                        { list: "ordered" },
                        { list: "bullet" },
                        { indent: "-1" },
                        { indent: "+1" },
                      ],
                      ["link", "image", "video"],
                      ["clean"],
                    ],
                  }}
                  placeholder="Enter message content..."
                  title="Enter the message content"
                  style={{
                    border: "1px solid #ddd",
                    minHeight: "200px",
                    maxHeight: "200px",
                    overflow: "auto",
                  }}
                />
                {errors.message && (
                  <div className="text-red-500">{errors.message}</div>
                )}
              </div>

              <div className="form-group col-12 col-lg-4 mb-4">
                <input
                  type="text"
                  name="description"
                  placeholder="Notes (Optional)"
                  value={description}
                  onChange={handleInputChange}
                  className="form-control custom-height"
                  title="Enter a Notes"
                />
              </div>
              <div className="flex justify-end mt-4">
                <button
                  type="button"
                  className="bg-orange-500 text-white text-xs font-bold uppercase px-4 py-2 rounded shadow hover:shadow-lg focus:outline-none"
                  onClick={handleCloseModal}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-lightblue-500 text-white text-xs font-bold uppercase px-4 py-2 ml-2 rounded shadow hover:shadow-lg focus:outline-none custom-button"
                >
                  Update
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </>
  );
};

export default UpdateMessageLogModal;
